import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { X, Plane, Calendar, MapPin, Users, Loader2, Check, UserPlus } from 'lucide-react';
import CityAutocomplete from './CityAutocomplete';

/**
 * Modern theme trip creation modal with dark styling.
 * Includes title, date range, destination, executive selection, and collaborators.
 */
const CreateTripModal2 = ({ onClose, onTripCreated, executives = [] }) => {
  const { user } = useAuth();
  const [tripTitle, setTripTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [destination, setDestination] = useState('');
  const [selectedExecutiveIds, setSelectedExecutiveIds] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOrgUsers();
  }, []);

  const loadOrgUsers = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/users`, { headers });
      setOrgUsers(response.data);
    } catch (error) {
      console.error('Failed to load organization users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleExecutive = (execId) => {
    setSelectedExecutiveIds(prev =>
      prev.includes(execId)
        ? prev.filter(id => id !== execId)
        : [...prev, execId]
    );
  };

  const getExecutiveName = (exec) => {
    return exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim() || 'Unnamed Executive';
  };

  const handleCollaboratorToggle = (userId) => {
    setSelectedCollaborators(prev => {
      const newSelected = { ...prev };
      if (newSelected[userId]) {
        delete newSelected[userId];
      } else {
        newSelected[userId] = 'view_only'; // Default permission
      }
      return newSelected;
    });
  };

  const handlePermissionChange = (userId, permission) => {
    setSelectedCollaborators(prev => ({
      ...prev,
      [userId]: permission
    }));
  };

  // Filter out current user from collaborators list
  const availableCollaborators = orgUsers.filter(u => u.id !== user?.id);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tripTitle.trim()) {
      setError('Trip title is required');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      // Build collaborators array
      const collaborators = Object.entries(selectedCollaborators).map(([userId, permission]) => ({
        user_id: userId,
        permission_level: permission
      }));

      const payload = {
        id: crypto.randomUUID(),
        title: tripTitle.trim(),
        status: 'planning',
        start_date: startDate || null,
        end_date: endDate || null,
        destination: destination || null,
        executive_ids: selectedExecutiveIds,
        collaborators: collaborators
      };

      const response = await axios.post(`${apiBase}/trips`, payload, { headers });

      if (onTripCreated) {
        onTripCreated(response.data);
      }

      onClose();
    } catch (error) {
      console.error('Failed to create trip:', error);
      setError(error.response?.data?.detail || 'Failed to create trip. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-overlay/60 backdrop-blur-sm overflow-y-auto h-full w-full z-[70] flex items-center justify-center p-4">
      <div className="relative w-full max-w-lg bg-ui-raised border border-ui-divider rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ui-divider">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-ui-blue-strong to-ui-teal-strong">
              <Plane className="w-5 h-5 text-ui-on-primary" />
            </div>
            <h2 className="text-xl font-semibold text-ui-ink">Create New Trip</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Trip Title */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              Trip Title <span className="text-ui-red-fg">*</span>
            </label>
            <input
              type="text"
              value={tripTitle}
              onChange={(e) => setTripTitle(e.target.value)}
              className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink placeholder-ui-faint focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent transition-all"
              placeholder="e.g., NYC Business Conference"
              required
              autoFocus
            />
          </div>

          {/* Destination */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Destination <span className="text-ui-muted text-xs font-normal">(optional)</span>
            </label>
            <CityAutocomplete
              value={destination}
              onChange={setDestination}
              placeholder="Search for a city..."
              className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink placeholder-ui-faint focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent transition-all"
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date <span className="text-ui-muted text-xs font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date <span className="text-ui-muted text-xs font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Executive Selection - Multiple */}
          {executives.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Executives <span className="text-ui-muted text-xs font-normal">(optional)</span>
              </label>
              <div className="bg-ui-sunk border border-ui-control rounded-xl p-2 max-h-48 overflow-y-auto">
                {executives.map((exec) => {
                  const isSelected = selectedExecutiveIds.includes(exec.id);
                  return (
                    <button
                      key={exec.id}
                      type="button"
                      onClick={() => toggleExecutive(exec.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-ui-blue-bg text-ui-ink'
                          : 'text-ui-ink-2 hover:bg-ui-hover'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-ui-primary border-ui-primary'
                          : 'border-ui-control'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-ui-on-primary" />}
                      </div>
                      <span className="text-sm">{getExecutiveName(exec)}</span>
                    </button>
                  );
                })}
              </div>
              {selectedExecutiveIds.length > 0 && (
                <div className="mt-2 text-xs text-ui-link">
                  {selectedExecutiveIds.length} executive{selectedExecutiveIds.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}

          {/* Collaborators Section */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              <UserPlus className="w-4 h-4 inline mr-1" />
              Collaborators <span className="text-ui-muted text-xs font-normal">(optional)</span>
            </label>
            <p className="text-xs text-ui-ink-3 mb-3">
              Select other users who should have access to this trip.
            </p>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-ui-ink-3" />
              </div>
            ) : availableCollaborators.length === 0 ? (
              <p className="text-sm text-ui-muted text-center py-4">
                No other users in your organization yet.
              </p>
            ) : (
              <div className="bg-ui-sunk border border-ui-control rounded-xl p-2 max-h-48 overflow-y-auto">
                {availableCollaborators.map((orgUser) => {
                  const isSelected = !!selectedCollaborators[orgUser.id];
                  return (
                    <div key={orgUser.id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-ui-hover">
                      <button
                        type="button"
                        onClick={() => handleCollaboratorToggle(orgUser.id)}
                        className={`flex items-center gap-3 flex-1 transition-colors ${
                          isSelected ? 'text-ui-ink' : 'text-ui-ink-2'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-ui-primary border-ui-primary'
                            : 'border-ui-control'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-ui-on-primary" />}
                        </div>
                        <div className="text-left">
                          <div className="text-sm">
                            {orgUser.first_name} {orgUser.last_name}
                            {orgUser.is_organization_admin && (
                              <span className="ml-2 text-xs bg-ui-violet-bg text-ui-violet-fg px-2 py-0.5 rounded-full">
                                Org Admin
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-ui-muted">{orgUser.email}</div>
                        </div>
                      </button>
                      {isSelected && (
                        <select
                          value={selectedCollaborators[orgUser.id]}
                          onChange={(e) => handlePermissionChange(orgUser.id, e.target.value)}
                          className="text-xs bg-ui-hover border border-ui-control rounded-lg px-2 py-1 text-ui-ink-2 focus:outline-none focus:ring-1 focus:ring-ui-primary"
                        >
                          <option value="view_only">View Only</option>
                          <option value="edit_components">Edit Components</option>
                          <option value="full_admin">Full Admin</option>
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {Object.keys(selectedCollaborators).length > 0 && (
              <div className="mt-2 text-xs text-ui-link">
                {Object.keys(selectedCollaborators).length} collaborator{Object.keys(selectedCollaborators).length > 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-ui-red-bg border border-ui-red-line rounded-xl p-4 text-sm text-ui-red-fg">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="px-5 py-2.5 rounded-xl text-ui-ink-2 bg-ui-sunk hover:bg-ui-hover border border-ui-control transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !tripTitle.trim()}
              className="px-5 py-2.5 rounded-xl text-ui-on-primary bg-gradient-to-r from-ui-blue-strong to-ui-teal-strong hover:from-ui-blue-strong/90 hover:to-ui-teal-strong/90 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Trip'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTripModal2;
